// EnergyRentV2 — RENT ENERGY, as its own page (/energy/rent?wallet=<id>) (UI rebuild batch 4: the UI-rebuild lane's hand-off of
// 27 Oct 2026, adopted by the energy lane; spec docs/ui-rebuild/handoff/ENERGY.md on ui-rebuild/batch4-handoff). Astra's `rent`
// ("Rent energy"), the terms the desk says it takes (status.policy.topUpTerms — 1 hour to 30
// days), never Astra's "1 hour / 1 day".
//
// Astra's form anatomy: one card — the wallet, the energy, the term — then the price, then the one gold primary; from lg the
// quote beside it. THE COMMITMENT BOUNDARY is the lane's own two steps: "Check the price" asks the desk for a quote and mints the
// order's key with it; "Rent for N TRX" sends THAT quote — never what the form says at the click — and a term of more than a day
// is confirmed in full first. Editing anything drops the quote.
//
// ONE COPY: the state and every rule are screens/Energy.tsx's useTopUp — the store (one state per wallet, outside the page: leaving
// and coming back forgets nothing), price(), rent(), the in-flight guard, orderOutcome (checked, not trusted), topUpHeldBack, the
// quote's own age, the order on the desk found by its own key — and its lines (TopUpHeld, TopUpFields, TopUpNoAnswer, TopUpAnswer).
// What is this page's own: the wallet picker, the price box, the foot and the aside — each fact on a line of its own (his words, 1 Nov
// 2026: "check everything where it applies"): the price box is the energy, the term, the price, a day of it, the burn and the saving,
// never "56,800 energy · 1 hour" or "Burning: 11.93 TRX · saves 66.7%" run together; the spend and its limit are two facts.
import { Link, useSearchParams } from 'react-router-dom';
import type { EnergyStatus, EnergyWallet } from '../../../lib/api';
import { dailyCostSun, rateTrx } from '../../../lib/energyOrder';
import { Field } from '../../../components/Field';
import { FormAside, FormFoot, FormFrame, PRIMARY, QUIET } from '../../../components/v2/move/FormFrame';
import { DeskLines, EnergyFigure, EnergyNow, FactList, StateWord, TrxFigure, V2_SLABS, besideForm, hasReading, underForm } from '../../../components/v2/energy/parts';
import {
  AboutSpend, AboutTopUp, TopUpAnswer, TopUpFields, TopUpHeld, TopUpNoAnswer, WhyThisAmount, burnLabel, lasts, saves, trx,
  useDesk, useOrders, useTopUp, nameOf
} from '../../Energy';

export function EnergyRentV2() {
  const desk = useDesk();
  const { d, canChange, nowMs, fresh } = desk;
  const [search, setSearch] = useSearchParams();
  const walletId = Number(search.get('wallet')) || null;
  const w = d?.wallets.find((x) => x.id === walletId) ?? null;
  const all = useOrders();
  const sending = w !== null && (all.topUps[w.id]?.sending ?? false);
  const quote = w ? all.topUps[w.id]?.quoted?.quote ?? null : null;

  const aside = d && (
    <FormAside
      title="Quote"
      figure={quote && quote.ok && !quote.nothingToDo && quote.costSun !== null ? `${trx(quote.costSun)} TRX` : '—'}
      sub="An order with no answer may already be charged."
      facts={[
        ...(w ? [{ label: 'Energy now', value: <EnergyNow w={w} /> }] : []),
        // No reading — the chain unread, an activation on its way, not read yet: why, as a tag of its own (the list's rows say it there).
        ...(w && !hasReading(w) ? [{ label: 'State', value: <StateWord w={w} d={d} /> }] : []),
        { label: d.provider.error !== null ? 'Balance · last known' : 'TronSave balance', value: <TrxFigure value={trx(d.provider.balanceSun)} /> },
        { label: 'Spent in 24h', value: <TrxFigure value={trx(d.spend.countedSun)} /> },
        { label: 'Limit in 24h', value: <TrxFigure value={trx(d.spend.dailyCapSun)} /> },
      ]}
      help={<><AboutTopUp d={d} /><p><AboutSpend d={d} /></p></>}
    />
  );

  return (
    <FormFrame title="Rent energy" aside={besideForm(aside)} below={<>{underForm(aside)}<Link to="/energy" className="tap-44 btn-link text-meta">← Energy</Link></>}>
      <DeskLines desk={desk} />
      {d && (
        <>
          <WalletPick d={d} w={w} walletId={walletId} sending={sending}
            onPick={(id) => setSearch((p) => { const n = new URLSearchParams(p); if (id) n.set('wallet', id); else n.delete('wallet'); return n; }, { replace: true })} />
          {w
            ? <TopUpForm key={w.id} w={w} d={d} canChange={canChange} held={fresh?.held === true} nowMs={nowMs} />
            : <FormFoot note="Choose the wallet first."><button type="button" className={PRIMARY} disabled>Check the price</button></FormFoot>}
        </>
      )}
    </FormFrame>
  );
}

/** The wallet to top up: the desk's wallets, retired ones left out. Not while an order is on its way: what is on the page is that order. */
function WalletPick({ d, w, walletId, sending, onPick }: { d: EnergyStatus; w: EnergyWallet | null; walletId: number | null; sending: boolean; onPick: (id: string) => void }) {
  return (
    <Field label="Wallet">
      <select className="select" value={w ? String(w.id) : ''} disabled={sending} onChange={(e) => onPick(e.target.value)}>
        <option value="">Choose a wallet</option>
        {d.wallets.filter((x) => x.status !== 'retired' || x.id === walletId).map((x) => (
          <option key={x.id} value={String(x.id)}>{nameOf(d, x)}{x.status !== 'active' ? ` — ${x.status}` : ''}</option>
        ))}
      </select>
    </Field>
  );
}

/** Price a top-up, then place it — Energy.tsx's useTopUp and its lines, in Astra's form anatomy. The quote on screen IS the order. */
function TopUpForm({ w, d, canChange, held, nowMs }: { w: EnergyWallet; d: EnergyStatus; canChange: boolean; held: boolean; nowMs: number }) {
  const t = useTopUp({ w, d, canChange, held, nowMs });
  const { s, price, rent, quote, pricing, heldBack, rentShown, rentGold } = t;

  return (
    <>
      <TopUpHeld t={t} w={w} />
      <TopUpFields t={t} d={d} amountApart />

      {/* What became of the order that got no answer, FIRST — it is why the button says "the same order". */}
      <TopUpNoAnswer t={t} rentalsHere={false} slabs={V2_SLABS}
        rentalLink={(id) => <Link className="btn-link text-meta" to={`/energy?rental=${id}`}>Rental #{id}</Link>} />

      {/* words:ok — the desk's or the Books' own sentence, first clause on the line (Said) */}
      <TopUpAnswer t={t} rentals={d.rentals} rentalsHere={false} slabs={V2_SLABS}
        price={(quote) => (
          // THE PRICE — what the order is held to. A day of a term of days is a rate, to the sun (N-090).
          <div className="space-y-1 rounded-lg border border-stroke bg-panel-2 px-4 pb-3 pt-1">
            <FactList facts={[
              ['Energy', quote.energy === null ? <span className="text-tg-hint">—</span> : <EnergyFigure n={quote.energy} />],
              ['Term', lasts(quote.durationSec)],
              ['Price', <TrxFigure key="price" value={trx(quote.costSun)} big />],
              ...(dailyCostSun(quote) !== null ? [['A day', <TrxFigure key="day" value={rateTrx(String(dailyCostSun(quote)))} />] as const] : []),
              [burnLabel(quote), <TrxFigure key="burn" value={trx(quote.burnCostSun)} />],
              ['Saves', <span key="saves" className="font-mono tabular">{saves(quote.savingBps)}</span>],
            ]} />
            <WhyThisAmount quote={quote} />
            {!canChange && <p className="text-meta text-tg-hint">Ordering needs full access.</p>}
          </div>
        )} />

      <FormFoot note={s.unanswered !== null ? undefined : rentShown ? 'Paid now from the TronSave balance.' : 'Priced first; the order is held to it.'}>
        {/* After an order with no answer, pricing again is a NEW order — the button says so; the other sends the SAME one. */}
        <button type="button" className={`${rentGold ? QUIET : PRIMARY} disabled:opacity-50`} disabled={pricing || s.sending} onClick={() => void price()}>
          {t.priceWord(true)}
        </button>
        {rentShown && quote !== null && quote.costSun !== null && (
          <button type="button" className={`${rentGold ? PRIMARY : QUIET} disabled:opacity-50`} disabled={s.sending || heldBack !== null}
            title={heldBack ?? undefined} onClick={() => void rent()}>
            {t.rentWord(quote.costSun)}
          </button>
        )}
      </FormFoot>
    </>
  );
}
